/**
 * Helpers for building URLs that point at the NestJS backend.
 *
 * In normal (standalone) mode the frontend and backend share an origin and all
 * URLs are relative (`/api/...`). In embed mode (the app is loaded inside a
 * WordPress page) the host page sets `window.__BTS_EMBED_BACKEND_URL__` to the
 * absolute backend origin, and every backend URL must be prefixed with it —
 * otherwise the browser resolves the URL against the WordPress host.
 */

/**
 * Backend origin in embed mode (e.g. `https://xyz.example.de`), otherwise `''`.
 * Trailing slashes are stripped so callers can always append `/path`.
 */
export function getBackendUrl(): string {
  return (window.__BTS_EMBED_BACKEND_URL__ ?? '').replace(/\/+$/, '');
}

/** REST API base, e.g. `https://xyz.example.de/api` or `/api`. */
function getRestApiBase(): string {
  return `${getBackendUrl()}${import.meta.env.VITE_REST_ENDPOINT || '/api'}`;
}

/**
 * Build a full REST API URL from a path relative to the API base.
 * `restApiUrl('/seminar/123/ics')` → `https://xyz.example.de/api/seminar/123/ics`
 */
export function restApiUrl(path: string): string {
  return `${getRestApiBase()}${path.startsWith('/') ? '' : '/'}${path}`;
}

/**
 * Like {@link restApiUrl}, but with the `webcal://` scheme so calendar apps
 * subscribe to the feed. Always absolute (webcal has no relative form).
 * `webcalUrl('/calendar/x/ics')` → `webcal://xyz.example.de/api/calendar/x/ics`
 *
 * Falls back to the plain https URL if an absolute URL cannot be constructed
 * (opaque origin), which still yields a downloadable .ics file.
 */
export function webcalUrl(path: string): string {
  const httpUrl = restApiUrl(path);
  try {
    const url = new URL(httpUrl, window.location.origin);
    return `webcal://${url.host}${url.pathname}${url.search}`;
  } catch {
    return httpUrl;
  }
}

/**
 * MIME types that browsers can render without treating the file as an active
 * document. In particular, SVG, HTML, and XML must not be opened through a
 * blob URL: blob documents inherit the creator page's origin, so scripts in an
 * uploaded file would otherwise execute with the frontend/WordPress origin.
 */
const SAFE_INLINE_MIME_TYPES = new Set([
  'application/pdf',
  'image/avif',
  'image/bmp',
  'image/gif',
  'image/jpeg',
  'image/png',
  'image/webp',
]);

function isSafeInlineMimeType(mimeType: string): boolean {
  const normalizedMimeType = mimeType.split(';', 1)[0].trim().toLowerCase();
  return (
    SAFE_INLINE_MIME_TYPES.has(normalizedMimeType) ||
    normalizedMimeType.startsWith('audio/') ||
    normalizedMimeType.startsWith('video/')
  );
}

function downloadBlob(blobUrl: string, filename?: string): void {
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = filename ?? '';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/**
 * Fetch a protected backend file (routes behind `AuthenticatedGuard`) with the
 * caller's Bearer token and hand it to the browser as a blob URL.
 *
 * Plain `<a href>` navigations cannot carry an `Authorization` header, so in
 * JWT deployments (Keycloak) they would 401. Session-cookie deployments still
 * work through `credentials: 'include'`.
 *
 * - `mode: 'download'` saves the file under `filename`.
 * - `mode: 'open'` shows safe, passive media types in a new tab. Active or
 *   unknown content types are downloaded instead so uploaded scripts cannot
 *   inherit the frontend origin. The tab is opened synchronously (before the
 *   async fetch) so popup blockers don't interfere.
 *
 * Rejects on a non-2xx response; callers decide how to surface that.
 */
export async function fetchProtectedFile(
  path: string,
  options: {
    token?: string;
    filename?: string;
    mode: 'download' | 'open';
  },
): Promise<void> {
  const { token, filename, mode } = options;
  const newTab = mode === 'open' ? window.open('', '_blank') : null;
  if (newTab) {
    newTab.opener = null;
  }

  try {
    const response = await fetch(restApiUrl(path), {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!response.ok) {
      throw new Error(
        `Datei konnte nicht geladen werden (HTTP ${response.status})`,
      );
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const canOpenInline = mode === 'open' && isSafeInlineMimeType(blob.type);

    if (newTab && canOpenInline) {
      newTab.location.href = blobUrl;
    } else {
      newTab?.close();
      downloadBlob(blobUrl, filename);
    }
    // Give the browser a moment to start using the URL before revoking it.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch (err) {
    newTab?.close();
    throw err;
  }
}
